/**
 * Active report context — the currently-selected (borefield, bore, job)
 * triple that pages across the app consume to scope their data.
 *
 * Lifted out of Sidebar's local `useState<ReportContext>` (where it was
 * unreachable to non-sidebar pages) per a PO scenario on 2026-06-02:
 * the new TRT v2 (Beta) page could not see that the user had Motor City
 * > Bore1 > Construction selected in the sidebar, because that selection
 * lived in Sidebar's component-local state.
 *
 * Consumers (current):
 *   - Sidebar (read + write — sets the context on bore/job pick + clears
 *     it via the × button)
 *   - features/projects/projectSlice.ts (read — synthesizes the R_o-k
 *     project + scenario shapes from the active bore + its mud-log
 *     report for the Bourdet TRT 1:1 port)
 *
 * This slice intentionally stores the *full* objects (not just IDs)
 * because Sidebar wants to render bore + borefield names without
 * re-resolving against the borefield list. If that turns out to be
 * stale-prone (object reference doesn't match a borefield list mutation),
 * switch to ID-only and resolve on read in selectors. For now the local-
 * data flow (testDataGenerator → setLocalBorefields, single dispatch)
 * means the references stay fresh.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Borefield, Bore, Job } from "../../features/borefields/types";

export interface ReportContext {
  borefield: Borefield | null;
  bore: Bore | null;
  job: Job | null;
}

const initialState: ReportContext = {
  borefield: null,
  bore: null,
  job: null,
};

const reportContextSlice = createSlice({
  name: "reportContext",
  initialState,
  reducers: {
    setReportContext(_state, action: PayloadAction<ReportContext>) {
      return action.payload;
    },
    clearReportContext() {
      return initialState;
    },
  },
});

export const { setReportContext, clearReportContext } =
  reportContextSlice.actions;
export const reportContextReducer = reportContextSlice.reducer;

/** Active (borefield, bore, job) triple — any field can be null. */
export const selectReportContext = (state: {
  reportContext: ReportContext;
}): ReportContext => state.reportContext;
